/**
 * <quick-view> - shop-the-look hurtigkoeb fra promo-tiles (Figma 38:573 /
 * P448 qck-Products). Klik paa et produkt-thumbnail i en collection-promo
 * aabner et modal med produktets billede, titel, pris, variant-vaelger og
 * add-to-cart - UDEN at forlade kollektionssiden. Progressive enhancement:
 * uden JS er thumbnailet et almindeligt <a href> til PDP'en.
 *
 * Mekanik (genbruger den delte util + eksisterende komponenter, intet
 * duplikeres):
 *  - fetchSection('quick-view-product', { url: produktets URL, signal }) henter
 *    den server-renderede buy box; hele #shopify-section-quick-view-product-
 *    wrapperen indsaettes, saa den injicerede <variant-picker> kan finde sin
 *    section (closest) og re-rendere pris/knap ved variant-skift.
 *  - <variant-picker> lazy-importeres foerst naar et modal med varianter
 *    aabnes (data-variant-picker-src).
 *  - Add-to-cart: cart-drawer.js' globale onProductSubmit fanger
 *    {% form 'product' %}-submit, tilfoejer og aabner kurv-draweren. Ved et
 *    lykket add lukker quick-view sig paa 'cart:added' (se connectedCallback).
 *  - Triggere: promo-thumbnails (9.9) og knapperne i PDP'ens complementary-
 *    blok (9.13). Alle baerer data-quick-view-trigger + data-product-url.
 *  - Penge server-ejet: pris kommer renderet retur via | money.
 *
 * A11y: native <dialog>.showModal() giver focus-trap, Escape og inert baggrund;
 * focus-retur til triggeren + aria oven paa. Persistent aria-live annoncerer
 * det indlaeste produkt.
 */
import { fetchSection, abortable, latestBusy, SectionRenderError } from '@theme/section-render';

class QuickView extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-quick-view-dialog]');
    this.content = this.querySelector('[data-quick-view-content]');
    this.errorBox = this.querySelector('[data-quick-view-error]');
    this.live = this.querySelector('[data-quick-view-live]');
    this.opener = null;
    this.nextSignal = abortable();
    this.markBusy = latestBusy((busy) => this.toggleAttribute('aria-busy', busy));

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => {
      // Afbryd in-flight, saa et sent svar ikke repainter en lukket modal, og
      // ryd indholdet saa reopen ALDRIG viser forrige produkt.
      this.nextSignal();
      this.content.replaceChildren();
      if (this.live) this.live.textContent = '';
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-quick-view-close]')) this.close();
    });
    // Lykket add herfra: cart-drawer.js sender 'cart:added' fra submit-knappen
    // lige foer kurven aabner. Quick-view lukker, saa kurven staar alene
    // (ejerbeslutning 2026-09-11, afloeser 9.9's "bliver liggende bag kurven"),
    // og triggeren overdrages som kurvens opener, saa fokus vender tilbage til
    // den naar kurven lukkes. Et fejlet add sender intet - modalet bliver.
    this.addEventListener('cart:added', (event) => {
      if (this.opener && document.contains(this.opener)) event.detail.opener = this.opener;
      this.close();
    });

    QuickView.instance = this;
    if (!QuickView.documentBound) {
      document.addEventListener('click', QuickView.onTriggerClick);
      QuickView.documentBound = true;
    }
  }

  async open(opener) {
    if (opener) this.opener = opener;
    const url = opener && opener.dataset.productUrl;
    if (!url) return;
    if (!this.dialog.open) this.dialog.showModal();
    this.#clearError();
    const doneBusy = this.markBusy();
    try {
      const html = await fetchSection('quick-view-product', { url, signal: this.nextSignal() });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('#shopify-section-quick-view-product');
      if (!section) throw new SectionRenderError('Quick view unavailable', 204);
      this.content.replaceChildren(document.importNode(section, true));
      // Lazy-upgrade variant-picker foerst naar et modal med varianter aabnes.
      // Fejler importen, er fallback-selecten skjult (den er gated bag html.js
      // for at undgaa et glimt foer chipsene) og chipsene uden funktion - saa
      // formen ville POST'e den forvalgte variant. Flaget giver selecten
      // tilbage. Modalens indhold ankommer efter 'load', saa theme.liquid's
      // failsafe daekker det ikke - den hoerer her.
      if (this.dataset.variantPickerSrc && this.content.querySelector('variant-picker:not(:defined)')) {
        import(this.dataset.variantPickerSrc).catch(() => {
          this.content
            .querySelectorAll('variant-picker:not(:defined)')
            .forEach((el) => el.setAttribute('data-picker-failed', ''));
        });
      }
      this.#focusContent();
      this.#announce();
      this.#clearError();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#showError(error);
    } finally {
      doneBusy();
    }
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  // Flyt fokus ind i det indlaeste indhold (foerste interaktive), saa
  // tastatur-brugere lander i buy box'en efter async-load.
  #focusContent() {
    const focusable = this.content.querySelector(
      'a[href], button:not([disabled]), select, input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable) {
      focusable.focus();
    } else {
      const close = this.querySelector('[data-quick-view-close]');
      if (close) close.focus();
    }
  }

  #announce() {
    if (!this.live) return;
    const title = this.content.querySelector('[data-quick-view-title]');
    const message = title ? title.textContent.trim() : '';
    this.live.textContent = '';
    requestAnimationFrame(() => {
      this.live.textContent = message;
    });
  }

  #showError(error) {
    // Samme regel som i cart-drawer: kun kundeskrevne beskeder vises. "Quick
    // view unavailable" og en raa netvaerksfejl er diagnostik, og den ville
    // desuden staa paa ENGELSK foran en dansk eller tysk kunde.
    const fallback = this.dataset.textError || '';
    const message = error && error.customerFacing && error.message ? error.message : fallback;
    if (error && !error.customerFacing) console.warn('[quick-view]', error);
    if (this.errorBox) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }
  }

  #clearError() {
    if (this.errorBox) {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }
  }

  static onTriggerClick(event) {
    const trigger = event.target.closest('[data-quick-view-trigger]');
    if (!trigger) return;
    const view = QuickView.instance;
    if (!view) return; // ingen enhancement-target -> linket navigerer til PDP
    event.preventDefault();
    view.open(trigger);
  }
}

if (!customElements.get('quick-view')) {
  customElements.define('quick-view', QuickView);
}
